import React, { useState, useEffect } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  Typography,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { styled } from "@mui/system";

// MUI Icons
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import WbTwilightIcon from "@mui/icons-material/WbTwilight";
import CalculateIcon from "@mui/icons-material/Calculate";
import GolfCourseIcon from "@mui/icons-material/GolfCourse";
import PublicIcon from "@mui/icons-material/Public";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";

// Custom Components
import MainLogo from "./MainLogo";
import MainTitle from "./MainTitle";
import MainOption from "./MainOption";
import MainSlider from "./MainSlider";
import MainButton from "./MainButton";
import MainComparison from "./MainComparison";
import MainReusableBox from "./MainReusableBox";

// Assets
import mainLogo from "../../assets/logo-main.png";
import mainBg from "../../assets/bg-main.jpeg";

const theme = createTheme({
  palette: {
    primary: { main: "#0139AC" },
    background: { default: "#f5f5f5" },
    error: { main: "#f44336" },
  },
});

// Fetch prompts
const FETCH_REGIONAL_MESSAGE =
  "Fetch results first to know the center comparison.";
const FETCH_NATIONAL_MESSAGE =
  "Fetch results first to know the national comparison.";
const FETCH_AI_MESSAGE =
  "Fetch results first to know the frequency of artificial insemination to fetch good results.";

const REGIONAL_AVERAGES = {
  CLSU: 37.74349607,
  CMU: 8.956185567,
  CSU: 6.085595872,
  DMMMSU: 24.79338843,
  LCSF: 19.08665105,
  MLPC: 19.07821848,
  MMSU: 26.31798291,
  UPLB: 15.82490041,
  USF: 18.08952987,
  USM: 13.69164378,
  VSU: 15.16521415,
  WVSU: 22.72834275,
};
const NATIONAL_AVERAGE = 22.24870778;

const regionOptions = [
  { id: "1", title: "I", value: "I" },
  { id: "2", title: "II", value: "II" },
  { id: "3", title: "CAR", value: "CAR" },
  { id: "4", title: "III", value: "III" },
  { id: "5", title: "IV-A", value: "IV-A" },
  { id: "6", title: "V", value: "V" },
  { id: "7", title: "IV-B", value: "IV-B" },
  { id: "8", title: "VIII", value: "VIII" },
  { id: "9", title: "VII", value: "VII" },
  { id: "10", title: "VI", value: "VI" },
  { id: "11", title: "X", value: "X" },
  { id: "12", title: "IX", value: "IX" },
  { id: "13", title: "XI", value: "XI" },
  { id: "14", title: "XII", value: "XII" },
  { id: "15", title: "BARMM", value: "BARMM" },
];

const centerOptions = [
  { id: "1", title: "CLSU", value: "CLSU" },
  { id: "2", title: "CMU", value: "CMU" },
  { id: "3", title: "CSU", value: "CSU" },
  { id: "4", title: "DMMMSU", value: "DMMMSU" },
  { id: "5", title: "LCSF", value: "LCSF" },
  { id: "6", title: "MLPC", value: "MLPC" },
  { id: "7", title: "MMSU", value: "MMSU" },
  { id: "8", title: "UPLB", value: "UPLB" },
  { id: "9", title: "USF", value: "USF" },
  { id: "10", title: "USM", value: "USM" },
  { id: "11", title: "VSU", value: "VSU" },
  { id: "12", title: "WVSU", value: "WVSU" },
];

const Dashboard = () => {
  // Controls state
  const [region, setRegion] = useState(regionOptions[0].value);
  const [center, setCenter] = useState(centerOptions[0].value);
  const [selectedHeat, setSelectedHeat] = useState("NH");
  const [selectedSeason, setSelectedSeason] = useState("A");
  const [distance, setDistance] = useState(10);

  // Results & UI state
  const [probability, setProbability] = useState(null);
  const [inseminations, setInseminations] = useState(null);
  const [isBold, setIsBold] = useState(false);
  const [animationTrigger, setAnimationTrigger] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Predictions CSV data
  const [predictionsData, setPredictionsData] = useState([]);

  // Load predictions.csv once on mount using native parser
  useEffect(() => {
    fetch('/predictions.csv')
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then(text => {
        const [headerLine, ...lines] = text.trim().split('\n');
        const headers = headerLine.split(',');
        const data = lines.map(line => {
          const cols = line.split(',');
          const obj = {};
          headers.forEach((h, i) => {
            const val = cols[i];
            if (h === 'Distance' || h === 'Probability (%)') {
              obj[h] = parseFloat(val);
            } else {
              obj[h] = val;
            }
          });
          return obj;
        });
        setPredictionsData(data);
      })
      .catch(() => setError('Could not load or parse predictions.csv'));
  }, []);

  // Handlers
  const handleRegionChange = e => {
    setRegion(e.target.value);
    setCenter(centerOptions[0].value);
  };
  const handleCenterChange = e => setCenter(e.target.value);
  const handleHeatChange = e => setSelectedHeat(e.target.value);
  const handleSeasonChange = e => setSelectedSeason(e.target.value);
  const handleDistanceChange = (e, newValue) => setDistance(newValue);

  // Lookup instead of backend fetch
  const fetchProbability = () => {
    setAnimationTrigger(false);
    if (!selectedHeat || !selectedSeason || !region || !center || distance == null) {
      setError("Please fill in all fields before calculating.");
      setProbability(null);
      setInseminations(null);
      setIsBold(false);
      return;
    }
    setLoading(true);
    setError(null);
    setProbability(null);
    setInseminations(null);
    setIsBold(false);

    const match = predictionsData.find(row =>
      row['Type of Heat'] === selectedHeat &&
      row['Season of AI'] === selectedSeason &&
      row['Distance'] === distance
    );
    if (!match) {
      setError('No data for that combination.');
      setLoading(false);
      return;
    }

    const fetchedProbability = match['Probability (%)'];
    if (typeof fetchedProbability !== 'number' || isNaN(fetchedProbability)) {
      setError('Invalid probability in CSV.');
      setLoading(false);
      return;
    }
    const n = fetchedProbability / 100;
    let k;
    if (n > 0 && n < 1) {
      k = Math.ceil(Math.log(0.5) / Math.log(1 - n));
    } else {
      k = '∞';
    }

    setTimeout(() => {
      setProbability(fetchedProbability);
      setInseminations(k);
      setIsBold(true);
      setAnimationTrigger(true);
      setLoading(false);
    }, 500);
  };

  // Renderers
  const renderRegionalComparison = () => {
    if (probability === null) return FETCH_REGIONAL_MESSAGE;
    const avg = REGIONAL_AVERAGES[center];
    if (probability > avg) return "The probability of success is higher than the center average.";
    if (probability < avg) return "The probability of success is lower than the center average.";
    return "The probability of success is equal to the center average.";
  };
  const renderNationalComparison = () => {
    if (probability === null) return FETCH_NATIONAL_MESSAGE;
    if (probability > NATIONAL_AVERAGE) return "The probability of success is higher than the national average.";
    if (probability < NATIONAL_AVERAGE) return "The probability of success is lower than the national average.";
    return "The probability of success is equal to the national average.";
  };
  const renderInseminationsMessage = () => {
    if (inseminations === null) return FETCH_AI_MESSAGE;
    if (inseminations === '∞') return "It is not possible to achieve a 50% success rate with the current probability.";
    return `${inseminations} AI services may be needed to achieve positive results`;
  };

  // Help texts
  const heatHelpText = (
    <Box p={1}>
      <Typography variant="body2">NH - Natural Heat</Typography>
      <Typography variant="body2">ES - Estrus Synchronization</Typography>
    </Box>
  );
  const seasonHelpText = (
    <Box p={1}>
      <Typography variant="body2">A: Cool Dry (Dec-Feb)</Typography>
      <Typography variant="body2">B: Hot Dry (Mar-May)</Typography>
      <Typography variant="body2">C: Rainy (Jun-Nov)</Typography>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <StyledContainer>
        {/* Header */}
        <Box display="flex" alignItems="center" mb={3}>
          <MainLogo mainLogo={mainLogo} />
          <MainTitle sx={{ ml: 2 }} />
        </Box>

        <Grid container spacing={4}>
          {/* Controls */}
          <Grid item xs={12} lg={6}>
            <MainReusableBox sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <MainOption
                icon={<LocalFireDepartmentIcon />}
                title="Type of Heat"
                subtitle="Choose the heat type applicable to your scenario."
                data={[
                  { id: '1', title: 'NH', value: 'NH' },
                  { id: '2',	title: 'ES', value: 'ES' },
                ]}
                selectedValue={selectedHeat}
                onChange={handleHeatChange}
                helpText={heatHelpText}
              />
              <MainOption
                icon={<WbTwilightIcon />}
                title="Type of Season"
                subtitle="Select the season type for accurate results."
                data={[
                  { id: '1', title: 'A', value: 'A' },
                  { id: '2', title: 'B', value: 'B' },
                  { id: '3', title: 'C', value: 'C' },
                ]}
                selectedValue={selectedSeason}
                onChange={handleSeasonChange}
                helpText={seasonHelpText}
              />
              <MainSlider
                title="Set a Distance (km)"
                icon={<GolfCourseIcon />}
                value={distance}
                onChange={handleDistanceChange}
                min={1}
                max={100}
              />
              <MainButton
                title="Calculate"
                icon={<CalculateIcon />}
                onClick={fetchProbability}
                disabled={!selectedHeat || !selectedSeason || !region || !center || distance == null}
              />
            </MainReusableBox>
          </Grid>

          {/* Results */}
          <Grid item xs={12} lg={6} display="flex" flexDirection="column">
            <MainReusableBox sx={{ mb: 2, flex: 1 }}>
              <MainComparison
                title="Regional Comparison"
                result={renderRegionalComparison()}
                Icon={PublicIcon}
                isBold={isBold}
                trigger={animationTrigger}
                secs={0.8}
              />
            </MainReusableBox>
            <MainReusableBox sx={{ flex: 1 }}>
              <MainComparison
                title="AI Frequency"
                result={renderInseminationsMessage()}
                Icon={MedicalServicesIcon}
                isBold={isBold}
                trigger={animationTrigger}
                secs={1.4}
              />
            </MainReusableBox>
          </Grid>
        </Grid>

        {/* Loading & Error */}
        {loading && (
          <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
            <CircularProgress color="primary" />
          </Box>
        )}
        {error && (
          <Box sx={{ position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)', bgcolor: 'error.main', color: '#fff', px: 3, py: 1.5, borderRadius: 1 }}>
            <Typography>{error}</Typography>
          </Box>
        )}
      </StyledContainer>
    </ThemeProvider>
  );
};

export default Dashboard;

const StyledContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: '5%',
  minHeight: '100vh',
  backgroundImage: `url(${mainBg})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  overflowY: 'auto',
}));
